/***
 *
 *   VIEW
 *   The view houses global components that are common to all views
 *   (notification, modal), handles errors and renders the layout
 *
 **********/

import { useEffect, useState, createContext, type ReactNode } from 'react';
import { AppLayout, AuthLayout, HomeLayout, OnboardingLayout, Modal, Notification, useNavigate } from '../lib';
import ReportIssue from 'components/sentry/report-issue';
import { UMLoader } from '@urbanmetrix/um-kit';
import { getApiErrorMessage } from 'api/apiClient';
import { BrowserCompatibilityDialog } from 'components/browser/BrowserCompatibilityDialog';

// import './scss/normalize.scss';
// import './scss/view.scss';
// import './scss/typography.scss';

export type NotificationFormat = string | null;

const BROWSER_POPUP_SEEN_KEY = 'urbanmetrix-browser-popup-seen';

function isChromeBrowser() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  const uaData = (
    navigator as Navigator & {
      userAgentData?: { brands?: Array<{ brand: string; version?: string }> };
    }
  ).userAgentData;
  if (uaData?.brands?.some(({ brand }) => brand === 'Google Chrome')) {
    return true;
  }
  const ua = navigator.userAgent || '';
  const isChromeIOS = /CriOS/i.test(ua);
  const isChrome = /Chrome/i.test(ua) && !/Edg|OPR|Opera|SamsungBrowser|DuckDuckGo/i.test(ua);
  return isChromeIOS || isChrome;
}

function hasSeenBrowserCompatibilityDialog() {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.sessionStorage.getItem(BROWSER_POPUP_SEEN_KEY) === 'true';
  } catch (_error) {
    return false;
  }
}

function markBrowserCompatibilityDialogAsSeen() {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(BROWSER_POPUP_SEEN_KEY, 'true');
  } catch (_error) {
    // Ignore storage access issues and keep startup usable.
  }
}

export interface NotificationData {
  text?: string;
  type?: string;
  show?: boolean;
  visible?: string;
  format?: NotificationFormat;
  icon?: string | null;
  autoclose?: boolean;
}

export interface ToastData extends NotificationData {
  id: string;
}

export interface ModalData {
  show?: boolean;
  title?: ReactNode;
  text?: ReactNode;
  buttonText?: string | null;
  url?: string | null;
  method?: string | null;
  callback?: ((form?: unknown, res?: unknown) => void) | null;
  form?: unknown;
  [key: string]: unknown;
}

export interface HandleErrorOptions {
  autoclose?: boolean;
}

export interface ViewNotificationApi {
  show: (text: string, type?: string, autoclose?: boolean, format?: NotificationFormat, icon?: string | null) => void;
  hide: (id?: string) => void;
  data: NotificationData;
}

export interface ViewModalApi {
  show: (content?: Partial<ModalData> | null, callback?: ModalData['callback']) => void;
  hide: (cancel?: boolean, res?: unknown) => void;
  refresh: React.Dispatch<React.SetStateAction<ModalData>>;
  data: ModalData;
}

export interface ViewContextValue {
  notification: ViewNotificationApi;
  modal: ViewModalApi;
  setLoading: (state: boolean) => void;
  handleError: (err: unknown, options?: HandleErrorOptions) => void;
}

export const ViewContext = createContext<ViewContextValue>({} as ViewContextValue);

interface ApiError {
  response?: {
    status?: number;
    data?: { inputError?: string; msg?: string; message?: string };
  };
  code?: string;
  status?: number;
}

interface ViewProps {
  title: string;
  layout?: 'app' | 'home' | 'auth' | 'onboarding' | 'plain';
  display?: React.ComponentType<Record<string, unknown>>;
  data?: Record<string, unknown>;
}

function PlainLayout({ children }: { children: React.ComponentType<Record<string, unknown>> }) {
  const Child = children;
  return <Child />;
}

export function View(props: ViewProps) {
  const navigate = useNavigate();

  // state
  const [notification, setNotification] = useState<NotificationData>({
    visible: 'hide',
    autoclose: true,
  });
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const [modal, setModal] = useState<ModalData>({});
  const [loading, setLoading] = useState(false);
  const [showBrowserCompatibilityDialog, setShowBrowserCompatibilityDialog] = useState(false);

  useEffect(() => {
    setShowBrowserCompatibilityDialog(!isChromeBrowser() && !hasSeenBrowserCompatibilityDialog());
  }, []);

  function handleCloseBrowserCompatibilityDialog() {
    markBrowserCompatibilityDialogAsSeen();
    setShowBrowserCompatibilityDialog(false);
  }

  // layouts
  const layouts = {
    app: AppLayout,
    home: HomeLayout,
    auth: AuthLayout,
    onboarding: OnboardingLayout,
    plain: PlainLayout,
  };

  // set title & layout
  document.title = props.title;
  let Layout = props.layout ? layouts[props.layout] : AppLayout;

  if (!props.display) return false;

  function showNotification(
    text: string,
    type?: string,
    autoclose?: boolean,
    format?: NotificationFormat,
    icon?: string | null
  ) {
    const resolvedFormat = format || 'toast';
    if (resolvedFormat.includes('toast')) {
      const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const toast = {
        id,
        text: text,
        type: type,
        show: true,
        format: resolvedFormat,
        icon: icon,
        autoclose: autoclose,
      };

      setToasts((items) => [...items, toast]);

      if (autoclose) setTimeout(() => removeToast(id), 5000);
      return;
    }

    setNotification({
      text: text,
      type: type,
      show: true,
      format: resolvedFormat,
      icon: icon,
      autoclose: autoclose,
    });

    if (autoclose) setTimeout(hideNotification, 3000);
  }

  function removeToast(id: string) {
    setToasts((items) => items.filter((item) => item.id !== id));
  }

  function hideNotification(id?: string) {
    if (id) {
      removeToast(id);
      return;
    }

    setNotification({
      text: '',
      type: '',
      show: false,
      format: null,
      icon: null,
      autoclose: true,
    });
  }

  function showModal(content?: Partial<ModalData> | null, callback?: ModalData['callback']) {
    let data: ModalData = { ...modal };
    if (content) {
      for (let key in content) data[key] = content[key];

      data.show = true;
      data.callback = callback;
    }

    setModal(data);
  }

  function hideModal(cancel?: boolean, res?: unknown) {
    // callback?
    if (!cancel && modal.callback) modal.callback(modal.form, res);

    // reset the modal
    setModal({
      title: null,
      text: null,
      buttonText: null,
      url: null,
      method: null,
      show: false,
    });
  }

  function handleError(err: unknown, options: HandleErrorOptions = {}) {
    const { autoclose = true } = options || {};
    let message = 'There was an error, please try again';
    let hasApiMessage = false;
    if (err) {
      const apiErr = err as ApiError;
      message = getApiErrorMessage(err, message);

      if (apiErr.response) {
        const apiMessage = getApiErrorMessage(err, '');
        if (apiMessage) {
          message = apiMessage;
          hasApiMessage = true;
        }

        if (apiErr.response.status) {
          switch (apiErr.response.status) {
            case 401:
              if (hasApiMessage) showNotification(message, 'error', autoclose);
              localStorage.clear();
              navigate('/login');
              break;
            case 403:
              showNotification(message, 'error', autoclose);
              break;
            case 404:
              navigate('/notfound');

              break;

            case 429:
              showNotification(message, 'error', autoclose);
              break;

            case 402: // payment required
              navigate('/account/subscription');
              break;

            default:
              showNotification(message, 'error', autoclose);
              break;
          }
        }
      } else {
        showNotification(message, 'error', autoclose);
      }
    }
  }

  const context: ViewContextValue = {
    notification: {
      show: showNotification,
      hide: hideNotification,
      data: notification,
    },
    modal: {
      show: showModal,
      hide: hideModal,
      refresh: setModal,
      data: modal,
    },

    setLoading: (state: boolean) => setLoading(state),
    handleError: handleError,
  };

  return (
    <ViewContext.Provider value={context}>
      {notification.show && <Notification data={notification} onClose={hideNotification} />}

      {toasts.some((toast) => toast.format?.includes('toast-left')) && (
        <div className="toast-stack toast-left">
          {toasts
            .filter((toast) => toast.format?.includes('toast-left'))
            .map((toast) => (
              <Notification key={toast.id} data={toast} onClose={() => hideNotification(toast.id)} />
            ))}
        </div>
      )}

      {toasts.some((toast) => !toast.format?.includes('toast-left')) && (
        <div className="toast-stack">
          {toasts
            .filter((toast) => !toast.format?.includes('toast-left'))
            .map((toast) => (
              <Notification key={toast.id} data={toast} onClose={() => hideNotification(toast.id)} />
            ))}
        </div>
      )}

      {loading && <UMLoader />}

      {modal.show && <Modal {...(modal as React.ComponentProps<typeof Modal>)} />}

      <BrowserCompatibilityDialog
        open={showBrowserCompatibilityDialog}
        onClose={handleCloseBrowserCompatibilityDialog}
      />

      <ReportIssue showNotification={showNotification} />

      <Layout title={props.title} data={props.data}>
        {props.display}
      </Layout>
    </ViewContext.Provider>
  );
}
